// data-fit LLC · DecisionFlow · EXECUTE Stage
// PayoutResultsViewer.jsx · DDL: R64 · API: v1.12 · Session S944
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: is_latest_final=true, generated fields read-only, numeric strings parsed
// S944 enhancements:
//   OI-S943-PAYROLL-VIEW-TOTAL     — server-side aggregate totals endpoint
//   OI-S943-PAYROLL-VIEW-FILTERS   — BU/Segment/Role/Motion/Manager filters + chips
//   OI-S943-PAYROLL-VIEW-HIERARCHY — manager-grouped list, team indented beneath, collapsible
//   OI-S943-PAYROLL-VIEW-PAGINATION — server-side pagination (page 25/50/100)
//   OI-S943-PAYROLL-VIEW-PERIOD-VS-ANNUAL — period code label on all monetary tiles
//   OI-S943-PAYROLL-VIEW-NO-POSITION-REF — "Name" label, role_code secondary, no UUID
//   OI-S935-MGR-PAYOUT-DETAIL-PANEL — rollup_commission/hybrid_commission rendered in panel
// Preserved: PayoutDetailPanel, ackMap, goNext, SCREEN_ORDER, formatMoney, SortIcon, error handling
// PATCH-S92-07: OI comment retained — USD hardcoding vs payout_currency_code
// M040 C2: hardcoded colors → tokens (carried forward)
// S574 fix: row.position_name (not row.full_name) carried forward
// S578: search, sort, clickable rows + PayoutDetailPanel carried forward
//
// S971 changes — OI-UI-13b + OI-UI-14:
//   UNIFIED ORG TREE VIEW: flat list + By Manager toggle removed. Single hierarchy
//     view always shown. /api/v1/payout-summaries/hierarchy drives all rendering.
//   DEFAULT EXPAND: depth 0 (VP) + depth 1 (MGR_L2) auto-expanded on load.
//     One click on MGR_L1 reveals ICs. Minimises clicks for 10k-scale orgs.
//   CONTEXTUAL SEARCH: client-side DFS over full in-memory tree. Match on name →
//     return matched nodes with full ancestor chain (for context) + direct children
//     (for manager positions). Pre-search expand state saved; restored on clear.
//     Non-matching branches hidden. Matched node name highlighted.
//   SCOPE TO MANAGER: ManagerTypeahead repurposed — selecting a manager scopes the
//     entire tree to that manager's branch as root (Salesforce Comp pattern).
//     Clears the scoped view on X. Independent of name search.
//   AGGREGATE TILES: "Commission payout" dropped (arr-only, misleads at headline).
//     Replaced with "Avg payout % OTE" (cost-efficiency signal). Tile order reframed:
//     Total payout → Avg payout % OTE → Avg attainment → Total quota → Total credit → Positions.
//   HIERARCHY COLUMNS: simplified — Name / Own Payout / Team Payout / Total / Payout % OTE.
//     ARR payout is IC-level detail, belongs in drill-down panel not org tree summary.
//     $0.00 cells for non-applicable roles replaced with '—' (rendered in cell, not column).
//   PAGINATION: removed from hierarchy view (full tree loaded once from /hierarchy endpoint).

import React, {
  useEffect, useMemo, useState, useCallback, useRef
} from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'
import {
  X, ChevronUp, ChevronDown, ChevronsUpDown,
  Search, ChevronRight, Filter, Users
} from 'lucide-react'
import PayoutExplanationPanel from '../shared/PayoutExplanationPanel'

// ---------------------------------------------------------------------------
// Utility helpers (unchanged from prior sessions)
// ---------------------------------------------------------------------------
function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected payout summary error.'
  if (error.code === 'AUTH_INSUFFICIENT_ROLE') return 'You do not have the comp_admin role required to view payouts.'
  if (error.code === 'FEATURE_UNAVAILABLE') return 'Payout results are unavailable for the current tenant tier.'
  return error.recovery_action || error.message || 'Unable to load payouts.'
}

function parseMoney(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatMoney(value, currencyCode) {
  // OI-EXEC-PAYOUT-CURRENCY: multi-currency Enterprise only; defaulting to USD for MVP
  const currency = currencyCode || 'USD'
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency, maximumFractionDigits: 2
  }).format(parseMoney(value))
}

function formatMoneyCompact(value, currencyCode) {
  const currency = currencyCode || 'USD'
  const n = parseMoney(value)
  if (Math.abs(n) >= 1_000_000) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency,
      notation: 'compact', maximumFractionDigits: 1
    }).format(n)
  }
  return formatMoney(value, currencyCode)
}

function formatPercent(value) {
  return `${(parseMoney(value) * 100).toFixed(2)}%`
}

// ---------------------------------------------------------------------------
// SortIcon — unchanged
// ---------------------------------------------------------------------------
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return (
    <ChevronsUpDown className="inline h-3 w-3 ml-1 text-[rgb(var(--color-text-muted))] opacity-40" />
  )
  return sortDir === 'asc'
    ? <ChevronUp className="inline h-3 w-3 ml-1 text-[rgb(var(--color-primary))]" />
    : <ChevronDown className="inline h-3 w-3 ml-1 text-[rgb(var(--color-primary))]" />
}

// ---------------------------------------------------------------------------
// PayoutDetailPanel — slide-in side panel (preserved + manager component fix)
// Fetches /api/v1/payout-summaries/{payout_summary_id} on open.
// OI-S935-MGR-PAYOUT-DETAIL-PANEL: rollup_commission + hybrid_commission now render
// a meaningful breakdown label instead of "No breakdown available".
// ---------------------------------------------------------------------------
function PayoutDetailPanel({ row, onClose, planName, planVersion }) {
  const [detail, setDetail]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const isOpen = !!row

  useEffect(() => {
    if (!row?.payout_summary_id) { setDetail(null); return }
    setLoading(true); setError(null); setDetail(null)
    fetch(`/api/v1/payout-summaries/${row.payout_summary_id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
      .then(data => setDetail(data))
      .catch(() => setError('Unable to load payout detail.'))
      .finally(() => setLoading(false))
  }, [row?.payout_summary_id])

  const fmt    = (v) => formatMoney(v, row?.payout_currency_code)
  const fmtPct = (v) => v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—'

  const payoutPctOte = useMemo(() => {
    if (detail?.total_payout && detail?.calculation_trace?.ote_midpoint) {
      const payout = Number(detail.total_payout)
      const ote    = Number(detail.calculation_trace.ote_midpoint)
      if (ote > 0) return `${((payout / ote) * 100).toFixed(2)}%`
    }
    if (row?.payout_as_pct_ote != null) {
      return `${(Number(row.payout_as_pct_ote) * 100).toFixed(2)}%`
    }
    return loading ? '…' : '—'
  }, [detail, row, loading])

  // Manager component type labels (OI-S935-MGR-PAYOUT-DETAIL-PANEL)
  const MGR_COMPONENT_LABELS = {
    rollup_commission:  'Rollup — team production',
    hybrid_commission:  'Hybrid — own production',
    arr:                'ARR',
    mbo:                'MBO',
    nps:                'NPS',
    adoption:           'Adoption',
  }

  function componentLabel(type) {
    return MGR_COMPONENT_LABELS[type?.toLowerCase()] ?? type ?? 'Other'
  }

  function renderComponentDetail(c, best) {
    const type = (c.component_type ?? '').toLowerCase()
    if (type === 'arr' || type === 'hybrid_commission') {
      if (best.quota == null) {
        return (
          <div className="col-span-3 text-[rgb(var(--color-text-muted))] italic">
            Rollup — attainment based on team credits
          </div>
        )
      }
      return (<>
        <div><span className="block">Quota</span>{best.quota}</div>
        <div><span className="block">Credit</span>{best.credit ?? '—'}</div>
        <div><span className="block">Attainment</span>
          {best.attainment_pct != null ? `${(Number(best.attainment_pct) * 100).toFixed(1)}%` : '—'}
        </div>
      </>)
    }
    if (type === 'rollup_commission') {
      return (
        <div className="col-span-3 text-[rgb(var(--color-text-muted))]">
          Team rollup — sum of direct and indirect report payouts
        </div>
      )
    }
    if (type === 'mbo') {
      return (<>
        <div><span className="block">Code</span>{best.mbo_code ?? '—'}</div>
        <div><span className="block">Scoring</span>{best.scoring_method ?? '—'}</div>
        <div><span className="block">Achieved</span>
          {best.achieved_flag === true ? 'Yes' : best.achieved_flag === false ? 'No' : '—'}
        </div>
      </>)
    }
    return <div className="col-span-3 italic text-[rgb(var(--color-text-muted))]">No breakdown available</div>
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div className={`fixed top-0 right-0 z-40 h-full w-96 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <div className="min-w-0 pr-3">
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-0.5">
              Payout Detail
            </p>
            <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">
              {row?.position_name ?? '—'}
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              {row?.role_code ?? ''}
              {(planName || planVersion) && ` · ${planName ?? ''}${planVersion ? ` v${planVersion}` : ''}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))] transition"
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
          {error && (
            <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">
              {error}
            </div>
          )}

          {/* Summary tiles from list row */}
          {row && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total Payout', value: formatMoney(row.total_payout, row.payout_currency_code) },
                { label: 'Payout % OTE', value: payoutPctOte },
                { label: 'Commission',   value: formatMoney(row.arr_payout,      row.payout_currency_code) },
                { label: 'MBO',          value: formatMoney(row.mbo_payout,      row.payout_currency_code) },
                { label: 'NPS',          value: formatMoney(row.nps_payout,      row.payout_currency_code) },
                { label: 'Adoption',     value: formatMoney(row.adoption_payout, row.payout_currency_code) },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">{label}</p>
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Component breakdown — S893 aggregation + OI-S935 manager types */}
          {detail?.components?.length > 0 && (() => {
            const grouped = {}
            for (const c of detail.components) {
              const type = c.component_type ?? 'Other'
              if (!grouped[type]) grouped[type] = { ...c, resolved_payout: 0, _best: null }
              grouped[type].resolved_payout += parseMoney(c.resolved_payout)
              const prev = parseMoney(grouped[type]._best?.resolved_payout ?? 0)
              if (parseMoney(c.resolved_payout) >= prev) grouped[type]._best = c
            }
            const aggregated = Object.values(grouped)
            return (
              <div>
                <p className="mb-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                  Component Breakdown
                </p>
                <div className="space-y-2">
                  {aggregated.map((c) => {
                    const best = c._best ?? c
                    return (
                      <div key={c.component_type} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                            {componentLabel(c.component_type)}
                          </span>
                          <span className="text-tok-sm font-bold text-[rgb(var(--color-text))]">
                            {formatMoney(c.resolved_payout, best.currency_code)}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {renderComponentDetail(c, best)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

          {/* Calculation trace */}
          {detail?.calculation_trace && (
            <div>
              <p className="mb-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                How It Was Calculated
              </p>
              <div className="space-y-1.5 text-tok-sm">
                {detail.calculation_trace.proof_steps?.map((step, i) => (
                  <div key={i} className={`flex justify-between py-1 ${step.is_result || step.is_total ? 'border-t border-[rgb(var(--color-border))] font-semibold' : ''}`}>
                    <span className="text-[rgb(var(--color-text-muted))]">{step.label}</span>
                    <span className={step.is_negative ? 'text-[rgb(var(--color-danger))]' : step.is_zero ? 'text-[rgb(var(--color-text-muted))]' : 'text-[rgb(var(--color-text))]'}>
                      {step.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// MultiSelectDropdown — reusable filter control
// ---------------------------------------------------------------------------
function MultiSelectDropdown({ label, options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    )
  }

  const activeCount = selected.length

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-tok-sm transition
          ${activeCount > 0
            ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/8 text-[rgb(var(--color-primary))]'
            : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
          }`}
      >
        {label}
        {activeCount > 0 && (
          <span className="ml-1 rounded-full bg-[rgb(var(--color-primary))] text-white text-tok-xs px-1.5 py-0.5 leading-none">
            {activeCount}
          </span>
        )}
        <ChevronDown className="h-3 w-3 ml-0.5" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[240px] max-h-64 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-lg">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))] italic">
              {placeholder ?? 'No options'}
            </p>
          ) : (
            options.map(opt => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                  className="rounded"
                />
                {opt.label}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ManagerTypeahead — type-ahead search for manager filter
// ---------------------------------------------------------------------------
function ManagerTypeahead({ fiscalPeriodId, value, onChange, onClear }) {
  const [query, setQuery]       = useState('')
  const [results, setResults]   = useState([])
  const [loading, setLoading]   = useState(false)
  const [open, setOpen]         = useState(false)
  const ref = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(
          `/api/v1/positions?role_category=manager&search=${encodeURIComponent(query)}&fiscal_period_id=${fiscalPeriodId}&limit=20`,
          { credentials: 'include' }
        )
        if (res.ok) {
          const data = await res.json()
          setResults(Array.isArray(data?.data) ? data.data : [])
          setOpen(true)
        }
      } catch (_) { /* non-fatal */ } finally { setLoading(false) }
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [query, fiscalPeriodId])

  if (value) {
    return (
      <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/8 text-tok-sm text-[rgb(var(--color-primary))]">
        <Users className="h-3 w-3" />
        {value.position_name}
        <button onClick={onClear} className="ml-1 hover:opacity-70" aria-label="Clear manager filter">
          <X className="h-3 w-3" />
        </button>
      </span>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
        <input
          type="text"
          placeholder="Filter by manager…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="pl-8 pr-3 py-1.5 rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] w-44"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 mt-1 z-50 w-64 max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-lg">
          {results.map(r => (
            <button
              key={r.position_id}
              type="button"
              onClick={() => { onChange(r); setQuery(''); setOpen(false) }}
              className="w-full text-left px-3 py-2 hover:bg-[rgb(var(--color-surface-muted))] text-tok-sm"
            >
              <span className="font-medium text-[rgb(var(--color-text))]">{r.position_name}</span>
              <span className="ml-2 text-[rgb(var(--color-text-muted))] text-tok-xs">{r.role_code}</span>
            </button>
          ))}
        </div>
      )}
      {loading && (
        <p className="absolute top-full left-0 mt-1 text-tok-xs text-[rgb(var(--color-text-muted))] px-2 py-1">
          Searching…
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AggregateTiles — server-side totals with graceful degradation
// OI-S943-PAYROLL-VIEW-TOTAL + OI-S943-PAYROLL-VIEW-PERIOD-VS-ANNUAL
//
// S971 OI-UI-13b: tile set reframed for comp admin executive read.
//   Prominence order: Total payout → Avg payout % OTE → Avg attainment →
//     Total quota → Total credit → Positions.
//   "Commission payout" (arr-only, excludes rollup) dropped from headline tiles.
//   "Avg payout % OTE" added: SUM(total_payout)/SUM(ote_reporting) from aggregate
//     endpoint (server-computed, S971 router patch). Primary cost-efficiency signal.
// ---------------------------------------------------------------------------
function AggregateTiles({ totals, loading, periodCode, positionCount }) {
  // Resolve avg payout % OTE — prefer server-computed value (rate6 string → float)
  const avgPayoutPctOte = totals.avg_payout_pct_ote != null
    ? `${(Number(totals.avg_payout_pct_ote) * 100).toFixed(1)}%`
    : null

  const avgAttainment = totals.attainment_avg != null
    ? `${(Number(totals.attainment_avg) * 100).toFixed(1)}%`
    : null

  // Tile definitions — ordered by decision relevance for comp admin
  // type: 'money' | 'pct' | 'count'
  const tiles = [
    {
      label: `Total payout (${periodCode})`,
      type:  'money',
      value: totals.total,
      prominence: true,   // larger text — primary outcome metric
    },
    {
      label: 'Avg payout % OTE',
      type:  'pct',
      value: avgPayoutPctOte,
      title: 'Total payout as a percentage of total OTE across all commissioned positions',
    },
    {
      label: 'Avg attainment',
      type:  'pct',
      value: avgAttainment,
      title: 'Weighted average attainment (total credit / total quota)',
    },
    {
      label: `Total quota (${periodCode})`,
      type:  'money',
      value: totals.quota,
    },
    {
      label: `Total credit (${periodCode})`,
      type:  'money',
      value: totals.credit,
    },
    {
      label:  'Positions',
      type:   'count',
      value:  positionCount,
    },
  ]

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {tiles.map(({ label, type, value, prominence, title }) => (
        <div
          key={label}
          title={title}
          className={`bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-4
            ${prominence ? 'ring-1 ring-[rgb(var(--color-primary))]/20' : ''}`}
        >
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-1 leading-snug">{label}</p>
          {loading ? (
            <div className="h-5 w-20 rounded bg-[rgb(var(--color-surface-muted))] animate-pulse" />
          ) : (
            <p className={`font-semibold text-[rgb(var(--color-text))] ${prominence ? 'text-tok-xl' : 'text-tok-lg'}`}>
              {type === 'money' && (value != null ? formatMoneyCompact(value) : '—')}
              {type === 'pct'   && (value != null ? value : '—')}
              {type === 'count' && (value != null ? Number(value).toLocaleString() : '—')}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ActiveFilterChips — removable chips for active filters
// ---------------------------------------------------------------------------
function ActiveFilterChips({ filters, filterOptions, onRemoveBu, onRemoveSegment, onRemoveRole, onRemoveMotion, onClearManager, onClearAll }) {
  const hasAny = filters.bu.length > 0 || filters.segment.length > 0 ||
                 filters.role.length > 0 || filters.motion.length > 0 ||
                 filters.manager_position_id != null

  if (!hasAny) return null

  const buMap      = Object.fromEntries((filterOptions.bus      ?? []).map(o => [o.value, o.label]))
  const segMap     = Object.fromEntries((filterOptions.segments ?? []).map(o => [o.value, o.label]))
  const roleMap    = Object.fromEntries((filterOptions.roles    ?? []).map(o => [o.value, o.label]))
  const motionMap  = Object.fromEntries((filterOptions.motions  ?? []).map(o => [o.value, o.label]))

  function Chip({ label, onRemove }) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))] text-tok-xs font-medium">
        {label}
        <button onClick={onRemove} className="hover:opacity-70" aria-label={`Remove filter ${label}`}>
          <X className="h-3 w-3" />
        </button>
      </span>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filters.bu.map(v => (
        <Chip key={`bu-${v}`} label={buMap[v] ?? v} onRemove={() => onRemoveBu(v)} />
      ))}
      {filters.segment.map(v => (
        <Chip key={`seg-${v}`} label={segMap[v] ?? v} onRemove={() => onRemoveSegment(v)} />
      ))}
      {filters.role.map(v => (
        <Chip key={`role-${v}`} label={roleMap[v] ?? v} onRemove={() => onRemoveRole(v)} />
      ))}
      {filters.motion.map(v => (
        <Chip key={`motion-${v}`} label={motionMap[v] ?? v} onRemove={() => onRemoveMotion(v)} />
      ))}
      {filters.manager_position_id && (
        <Chip label={`Mgr: ${filters.manager_position_id.position_name}`} onRemove={onClearManager} />
      )}
      <button
        type="button"
        onClick={onClearAll}
        className="text-tok-xs text-[rgb(var(--color-text-muted))] underline hover:text-[rgb(var(--color-text))] ml-1"
      >
        Clear all
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// HierarchyRow — single row in unified org tree (S971)
//
// Column set: Name | Own Payout | Team Payout | Total | Payout % OTE
// ARR/MBO removed from tree — IC-level detail belongs in the drill-down panel.
//
// isManager: node has children in the data model (children array may be empty
//   after search pruning — use node._hasChildren to preserve toggle affordance
//   when children exist but are pruned by search).
//
// Row states:
//   _matched:    direct search match — name highlighted, normal weight
//   _ancestor:   ancestor of a match — subdued, force-expanded, non-interactive name
//   _directChild: direct child of a match — shown as context, lighter weight
//   (none):      normal unfiltered row
//
// Non-applicable cells render '—' in muted text, not '$0.00', per enterprise
// table conventions (empty ≠ zero; zero is a real value).
// ---------------------------------------------------------------------------
function HierarchyRow({ node, depth, expanded, onToggle, onSelect, selectedId, ackMap, periodCode, searchQuery }) {
  // A node is a manager if it has children in the FULL data (before search pruning).
  // node.children reflects the pruned set during search — use length > 0 for expand toggle
  // only when not searching; during search, _ancestor flag means children were pruned.
  const hasChildren  = node.children && node.children.length > 0
  const isManager    = hasChildren || node._ancestor  // _ancestor = had children, pruned by search
  const isExpanded   = expanded.has(node.position_id)
  const isSelected   = selectedId === node.payout_summary_id
  const indent       = depth * 20  // 20px per depth level

  // Search state flags set by searchTree()
  const isMatched    = !!node._matched
  const isAncestor   = !!node._ancestor
  const isDirectChild = !!node._directChild

  // Highlight matched substring in name
  function highlightName(name) {
    if (!searchQuery?.trim() || !isMatched) return name ?? '—'
    const q = searchQuery.trim()
    const idx = (name ?? '').toLowerCase().indexOf(q.toLowerCase())
    if (idx === -1) return name ?? '—'
    return (
      <>
        {name.slice(0, idx)}
        <mark className="bg-[rgb(var(--color-primary))]/20 text-[rgb(var(--color-primary))] rounded px-0.5">
          {name.slice(idx, idx + q.length)}
        </mark>
        {name.slice(idx + q.length)}
      </>
    )
  }

  // Own payout: for managers, own_payout (IC/hybrid personal component).
  // For ICs, total_payout = own_payout (no team component).
  const ownPayoutVal  = isManager ? (node.own_payout ?? node.total_payout) : node.total_payout
  // Team payout: only meaningful for manager nodes
  const teamPayoutVal = isManager ? (node.team_payout ?? 0) : null

  // Payout % OTE — computed from stored values
  // Payout % OTE — individual performance against personal OTE target.
  // MUST use own_payout (personal engine result), NOT total_payout.
  // total_payout in the hierarchy node = own_payout + team_payout (branch rollup).
  // Dividing branch total against ote_reporting gives inflated % for managers.
  // For ICs: ownPayoutVal === total_payout, so IC rows are unaffected.
  const payoutPctOte = node.ote_reporting && Number(node.ote_reporting) > 0
    ? `${((Number(ownPayoutVal) / Number(node.ote_reporting)) * 100).toFixed(2)}%`
    : '—'

  // Row style — ancestor rows visually subdued to emphasise matched descendants
  const rowClass = [
    'cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))]',
    isSelected  ? 'bg-[rgb(var(--color-primary))]/8'                              : '',
    isMatched   ? 'bg-[rgb(var(--color-primary))]/5'                              : '',
    isAncestor  ? 'opacity-60'                                                    : '',
    isManager && !isAncestor ? 'font-medium'                                      : '',
  ].filter(Boolean).join(' ')

  return (
    <>
      <tr onClick={() => onSelect(node)} className={rowClass}>

        {/* Name — indent + expand toggle + ack dot + name + role badge */}
        <td className="px-4 py-2 text-[rgb(var(--color-text))] min-w-[280px]">
          <div className="flex items-center gap-1.5" style={{ paddingLeft: `${indent}px` }}>
            {/* Expand/collapse toggle for manager nodes */}
            {isManager ? (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onToggle(node.position_id) }}
                className="shrink-0 rounded p-0.5 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded
                  ? <ChevronDown  className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="shrink-0 w-5" aria-hidden="true" />
            )}

            <span className="flex items-center gap-2 min-w-0">
              {/* Unacknowledged plan dot */}
              {ackMap[node.position_id] === false && (
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-[rgb(var(--color-warning))]"
                  title="Plan not yet acknowledged"
                />
              )}
              <span className="truncate">
                <span className="text-tok-sm">
                  {highlightName(node.position_name ?? node.employee_id)}
                </span>
                <span className="ml-2 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
                  {node.role_code}
                </span>
              </span>
            </span>
          </div>
        </td>

        {/* Own Payout — personal production for managers; total for ICs */}
        <td className="px-4 py-2 text-tok-sm text-[rgb(var(--color-text))] tabular-nums">
          {ownPayoutVal != null ? formatMoney(ownPayoutVal, node.payout_currency_code) : '—'}
        </td>

        {/* Team Payout — rollup for managers; '—' for ICs (not zero — genuinely N/A) */}
        <td className="px-4 py-2 text-tok-sm tabular-nums">
          {teamPayoutVal != null
            ? <span className="text-[rgb(var(--color-text))]">{formatMoney(teamPayoutVal, node.payout_currency_code)}</span>
            : <span className="text-[rgb(var(--color-text-muted))]">—</span>
          }
        </td>

        {/* Total — own + team for managers; own payout for ICs */}
        <td className="px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] tabular-nums">
          <span title="GENERATED COLUMN — read-only">
            {formatMoney(node.total_payout, node.payout_currency_code)}
          </span>
        </td>

        {/* Payout % OTE — computed; '—' when OTE unavailable */}
        <td className="px-4 py-2 text-tok-sm tabular-nums">
          {payoutPctOte !== '—'
            ? <span className="text-[rgb(var(--color-text))]">{payoutPctOte}</span>
            : <span className="text-[rgb(var(--color-text-muted))]">—</span>
          }
        </td>
      </tr>

      {/* Children — rendered when expanded and children exist */}
      {isManager && isExpanded && hasChildren && node.children.map(child => (
        <HierarchyRow
          key={child.position_id ?? child.payout_summary_id}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          onToggle={onToggle}
          onSelect={onSelect}
          selectedId={selectedId}
          ackMap={ackMap}
          periodCode={periodCode}
          searchQuery={searchQuery}
        />
      ))}
    </>
  )
}

// ---------------------------------------------------------------------------
// PaginationControls — server-side pagination
// OI-S943-PAYROLL-VIEW-PAGINATION
// ---------------------------------------------------------------------------
function PaginationControls({ page, pageSize, totalCount, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const from       = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const to         = Math.min(page * pageSize, totalCount)

  const pages = useMemo(() => {
    const p = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) p.push(i)
    } else {
      p.push(1)
      if (page > 3) p.push('…')
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) p.push(i)
      if (page < totalPages - 2) p.push('…')
      p.push(totalPages)
    }
    return p
  }, [page, totalPages])

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[rgb(var(--color-border))]">
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        {totalCount === 0 ? 'No positions' : `Showing ${from}–${to} of ${totalCount.toLocaleString()} positions`}
      </p>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="px-2 py-1 rounded border border-[rgb(var(--color-border))] text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ‹
        </button>
        {pages.map((p, i) => (
          p === '…'
            ? <span key={`ellipsis-${i}`} className="px-1 text-tok-sm text-[rgb(var(--color-text-muted))]">…</span>
            : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`min-w-[2rem] px-2 py-1 rounded border text-tok-sm transition
                  ${p === page
                    ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white'
                    : 'border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
                  }`}
              >
                {p}
              </button>
            )
        ))}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="px-2 py-1 rounded border border-[rgb(var(--color-border))] text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ›
        </button>

        <select
          value={pageSize}
          onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1) }}
          className="ml-3 rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-tok-sm text-[rgb(var(--color-text-muted))] px-2 py-1"
          aria-label="Rows per page"
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Column definitions — flat list
// ---------------------------------------------------------------------------
const FLAT_COLUMNS = [
  { key: 'position_name', label: 'Name',                numeric: false },
  { key: 'arr_payout',    label: 'Commission',           numeric: true  },
  { key: 'mbo_payout',    label: 'MBO',                 numeric: true  },
  { key: 'nps_payout',    label: 'NPS',                 numeric: true  },
  { key: 'total_payout',  label: 'Total Payout',        numeric: true  },
  { key: 'payout_as_pct_ote', label: 'Payout % OTE',   numeric: true  },
]

// Hierarchy view columns (own + team split for managers)
const HIER_COLUMNS = [
  { key: 'position_name', label: 'Name',        numeric: false },
  { key: 'arr_payout',    label: 'Commission',   numeric: true  },
  { key: 'mbo_payout',    label: 'MBO',         numeric: true  },
  { key: 'own_payout',    label: 'Own Payout',  numeric: true  },
  { key: 'team_payout',   label: 'Team Payout', numeric: true  },
  { key: 'total_payout',  label: 'Total',       numeric: true  },
  { key: 'payout_as_pct_ote', label: 'Payout % OTE', numeric: true },
]

// ---------------------------------------------------------------------------
// buildFilterQuery — shared filter param builder
// ---------------------------------------------------------------------------
function buildFilterParams(filters) {
  const p = new URLSearchParams()
  if (filters.bu.length)      p.set('bu_code',      filters.bu.join(','))
  if (filters.segment.length) p.set('segment_code', filters.segment.join(','))
  if (filters.role.length)    p.set('role_code',    filters.role.join(','))
  if (filters.motion.length)  p.set('motion_code',  filters.motion.join(','))
  if (filters.manager_position_id)
    p.set('manager_position_id', filters.manager_position_id.position_id)
  return p
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function PayoutResultsViewer({ fiscalPeriodId, planCode, planName, planVersion }) {
  const { token } = useAuth()

  // ── S971: unified org tree state (flat list + toggle removed) ────────────
  // Single source of truth: full nested tree from /hierarchy endpoint.
  // filteredTree is the client-side search/scope result rendered in the table.
  const [hierarchyData, setHierarchyData]       = useState([])   // full tree from API
  const [filteredTree,  setFilteredTree]         = useState([])   // search/scope result
  const [hierarchyLoading, setHierarchyLoading] = useState(false)
  const [hierarchyError,   setHierarchyError]   = useState('')
  const [expandedNodes,    setExpandedNodes]     = useState(new Set())
  // Pre-search expand snapshot — restored when search is cleared
  const preSearchExpandRef = useRef(null)

  // ── Search state (client-side tree filter) ────────────────────────────────
  const [search, setSearch] = useState('')

  // ── Scope-to-manager state (S971: replaces By Manager flat filter) ────────
  // Selecting a manager scopes the entire tree to that manager's branch as root.
  // Independent of name search — both can be active simultaneously.
  const [scopedManager, setScopedManager] = useState(null)  // position object | null

  // ── Legacy flat list state — retained for aggregate/ack fetches only ───────
  const [rows, setRows]             = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage]             = useState(1)
  const [pageSize, setPageSize]     = useState(50)
  const [sortCol, setSortCol]       = useState('position_name')
  const [sortDir, setSortDir]       = useState('asc')
  const [loading, setLoading]       = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // ── Aggregate totals ───────────────────────────────────────────────────────
  const [aggregateTotals, setAggregateTotals] = useState(null)
  const [aggregateLoading, setAggregateLoading] = useState(false)

  // ── Filters ────────────────────────────────────────────────────────────────
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState({
    bu: [], segment: [], role: [], motion: [], manager_position_id: null
  })
  const [filterOptions, setFilterOptions] = useState({
    bus: [], segments: [], roles: [], motions: []
  })

  // ── Side panel + ack ──────────────────────────────────────────────────────
  const [selectedRow, setSelectedRow] = useState(null)
  const [ackMap, setAckMap]           = useState({})

  // ── Derived ───────────────────────────────────────────────────────────────
  const periodId = useMemo(() => {
    const parsed = Number(fiscalPeriodId)
    return Number.isInteger(parsed) ? parsed : null
  }, [fiscalPeriodId])

  // Extract period code from planCode (e.g. FY2026_M01 → M01) or fallback
  const periodCode = useMemo(() => {
    if (planCode) {
      const match = planCode.match(/M\d{2}/i)
      if (match) return match[0].toUpperCase()
    }
    return 'period'
  }, [planCode])

  const activeFilterCount = useMemo(() =>
    filters.bu.length + filters.segment.length +
    filters.role.length + filters.motion.length +
    (filters.manager_position_id ? 1 : 0)
  , [filters])

  // ── Fetch filter option lists ─────────────────────────────────────────────
  useEffect(() => {
    if (!periodId) return
    async function fetchOptions() {
      try {
        const [busRes, segsRes, rolesRes, motionsRes] = await Promise.all([
          fetch(`/api/v1/business-units?is_active=true`, { credentials: 'include' }),
          fetch(`/api/v1/segments?is_active=true`,       { credentials: 'include' }),
          fetch(`/api/v1/roles?is_active=true`,          { credentials: 'include' }),
          fetch(`/api/v1/selling-motions?is_active=true`,{ credentials: 'include' }),
        ])
        const [busData, segsData, rolesData, motionsData] = await Promise.all([
          busRes.ok   ? busRes.json()    : { data: [] },
          segsRes.ok  ? segsRes.json()   : { data: [] },
          rolesRes.ok ? rolesRes.json()  : { data: [] },
          motionsRes.ok? motionsRes.json(): { data: [] },
        ])
        setFilterOptions({
          bus:      (busData?.data     ?? []).map(o => ({ value: o.bu_code      ?? o.code, label: o.bu_name      ?? o.name ?? o.bu_code })),
          segments: (segsData?.data    ?? []).map(o => ({ value: o.segment_code ?? o.code, label: o.segment_name ?? o.name ?? o.segment_code })),
          roles:    (rolesData?.data   ?? []).map(o => ({ value: o.role_code    ?? o.code, label: o.role_name    ?? o.name ?? o.role_code })),
          motions:  (motionsData?.data ?? []).map(o => ({ value: o.motion_code  ?? o.code, label: o.motion_name  ?? o.name ?? o.motion_code })),
        })
      } catch (_) { /* non-fatal — filters render empty */ }
    }
    fetchOptions()
  }, [periodId])

  // ── Fetch acknowledgment map ──────────────────────────────────────────────
  useEffect(() => {
    if (!periodId) return
    fetch(
      `/api/v1/plan-acknowledgments?fiscal_period_id=${encodeURIComponent(periodId)}&limit=500`,
      { credentials: 'include' }
    )
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.data) return
        const map = {}
        for (const r of data.data) { map[r.position_id] = r.acknowledged }
        setAckMap(map)
      })
      .catch(() => {})
  }, [periodId])

  // ── Fetch aggregate totals ─────────────────────────────────────────────────
  const fetchAggregateTotals = useCallback(async (currentFilters) => {
    if (!periodId) return
    setAggregateLoading(true)
    try {
      const fp = buildFilterParams(currentFilters)
      fp.set('fiscal_period_id', periodId)
      fp.set('is_latest_final', 'true')
      const res = await fetch(
        `/api/v1/payout-summaries/aggregate?${fp.toString()}`,
        { credentials: 'include' }
      )
      if (res.ok) {
        const data = await res.json()
        setAggregateTotals({
          arr:                 parseMoney(data.arr_total      ?? data.arr),
          mbo:                 parseMoney(data.mbo_total      ?? data.mbo),
          nps:                 parseMoney(data.nps_total      ?? data.nps),
          adoption:            parseMoney(data.adoption_total ?? data.adoption),
          total:               parseMoney(data.grand_total    ?? data.total),
          quota:               parseMoney(data.quota_total),
          credit:              parseMoney(data.credit_total),
          attainment_avg:      data.attainment_avg     != null ? Number(data.attainment_avg)     : null,
          // S971 OI-UI-13b: avg_payout_pct_ote — rate6 string from router, kept as string
          // for direct display. AggregateTiles multiplies ×100 and formats as %.
          avg_payout_pct_ote:  data.avg_payout_pct_ote != null ? data.avg_payout_pct_ote         : null,
          count:               data.position_count ?? null,
        })
      } else {
        // Endpoint not yet built — degrade gracefully
        setAggregateTotals(null)
      }
    } catch (_) {
      setAggregateTotals(null)
    } finally {
      setAggregateLoading(false)
    }
  }, [periodId])

  // ── Fetch hierarchy ────────────────────────────────────────────────────────
  // S971: /api/v1/payout-summaries (flat list) no longer called from this component.
  // /api/v1/payout-summaries/hierarchy is the sole data source.
  // S971: primary data fetch. Called on mount and on filter change.
  // Default expand (depth 0+1) is handled by the hierarchyData useEffect below —
  // not here — so scope/search overrides don't fight with the initial expand.
  const loadHierarchy = useCallback(async (currentFilters) => {
    if (!periodId) return
    setHierarchyLoading(true); setHierarchyError('')
    try {
      const fp = buildFilterParams(currentFilters)
      fp.set('fiscal_period_id', periodId)
      fp.set('is_latest_final', 'true')
      const res = await fetch(
        `/api/v1/payout-summaries/hierarchy?${fp.toString()}`,
        { credentials: 'include' }
      )
      if (!res.ok) {
        setHierarchyError('Unable to load hierarchy. Check that the /hierarchy endpoint is deployed.')
        return
      }
      const data = await res.json()
      const tree = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
      setHierarchyData(tree)
      // filteredTree + expandedNodes set by the hierarchyData useEffect below
    } catch (_) {
      setHierarchyError('Unable to load hierarchy.')
    } finally {
      setHierarchyLoading(false)
    }
  }, [periodId])

  // ── S971: initial load — hierarchy + aggregate on mount ───────────────────
  useEffect(() => {
    if (!periodId) return
    fetchAggregateTotals(filters)
    loadHierarchy(filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId])

  // ── S971: default expand state ─────────────────────────────────────────────
  // Depth 0 (VP) + depth 1 (MGR_L2) expanded on load.
  // Comp admin sees full management layer immediately; one click per MGR_L1 for ICs.
  const buildDefaultExpanded = useCallback((tree) => {
    const expanded = new Set()
    function walk(nodes, depth) {
      for (const node of nodes) {
        if (depth <= 1 && node.children?.length > 0) {
          expanded.add(node.position_id)
          walk(node.children, depth + 1)
        }
      }
    }
    walk(tree, 0)
    return expanded
  }, [])

  // Set filteredTree + default expand whenever hierarchyData changes
  // (initial load or filter-triggered reload).
  useEffect(() => {
    if (hierarchyData.length === 0) return
    setFilteredTree(hierarchyData)
    setExpandedNodes(buildDefaultExpanded(hierarchyData))
  }, [hierarchyData, buildDefaultExpanded])


  // Re-fetch when filters change (reset to page 1) — retained for aggregate refresh
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters)
    setPage(1)
    fetchAggregateTotals(newFilters)
    // S971: hierarchy reloads on filter change (scope may differ)
    loadHierarchy(newFilters)
  }, [fetchAggregateTotals, loadHierarchy])


  // ── S971: client-side contextual tree search ───────────────────────────────
  // DFS over full in-memory tree. Returns pruned tree where every returned node is
  // either: (a) a match, (b) an ancestor of a match (context), or (c) a direct
  // child of a match (one level below for manager positions).
  // Also returns a Set of position_ids that should be force-expanded (all ancestors).
  const searchTree = useCallback((nodes, query) => {
    const q = query.trim().toLowerCase()
    if (!q) return { tree: nodes, forceExpanded: new Set() }

    const forceExpanded = new Set()

    // Returns cloned node if node or any descendant matches; null otherwise.
    // directChildDepth: if > 0, include this node regardless (it's a direct child of a match)
    function filterNode(node, directChildDepth) {
      const nameMatch = (node.position_name ?? '').toLowerCase().includes(q)

      if (nameMatch) {
        // Match: include with direct children (one level) visible
        const directChildren = (node.children ?? []).map(child => ({
          ...child,
          children: [],           // direct children shown flat, not recursed
          _directChild: true,
        }))
        return { ...node, children: directChildren, _matched: true }
      }

      if (directChildDepth > 0) {
        // This node is a direct child of a match — include as context, no children
        return { ...node, children: [], _directChild: true }
      }

      // Not a match — recurse into children to find matches
      const matchedChildren = []
      for (const child of (node.children ?? [])) {
        const result = filterNode(child, 0)
        if (result) matchedChildren.push(result)
      }

      if (matchedChildren.length > 0) {
        // Ancestor of a match — include as context, force-expand
        forceExpanded.add(node.position_id)
        return { ...node, children: matchedChildren, _ancestor: true }
      }

      return null  // no match in this branch — prune
    }

    const tree = nodes.map(n => filterNode(n, 0)).filter(Boolean)
    return { tree, forceExpanded }
  }, [])

  // ── S971: apply search to current tree (with scope applied first) ─────────
  const applySearch = useCallback((query, baseTree) => {
    if (!query.trim()) {
      // Restore pre-search expand state
      if (preSearchExpandRef.current !== null) {
        setExpandedNodes(preSearchExpandRef.current)
        preSearchExpandRef.current = null
      }
      setFilteredTree(baseTree)
      return
    }
    // Save pre-search expand state (only on first keypress, not on each keystroke)
    if (preSearchExpandRef.current === null) {
      setExpandedNodes(prev => { preSearchExpandRef.current = new Set(prev); return prev })
    }
    const { tree, forceExpanded } = searchTree(baseTree, query)
    setFilteredTree(tree)
    // Force-expand all ancestor nodes so matched nodes are visible
    setExpandedNodes(prev => {
      const next = new Set(prev)
      for (const id of forceExpanded) next.add(id)
      return next
    })
  }, [searchTree])

  // ── S971: scope tree to a manager's subtree ───────────────────────────────
  // Finds the manager node in the full tree and returns it as a single-root tree.
  const scopeToManager = useCallback((tree, managerPositionId) => {
    if (!managerPositionId) return tree
    function find(nodes) {
      for (const node of nodes) {
        if (node.position_id === managerPositionId) return node
        const found = find(node.children ?? [])
        if (found) return found
      }
      return null
    }
    const root = find(tree)
    return root ? [root] : tree
  }, [])

  // ── S971: search input handler ────────────────────────────────────────────
  const searchDebounceRef = useRef(null)
  const handleSearchChange = useCallback((value) => {
    setSearch(value)
    clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      const base = scopedManager
        ? scopeToManager(hierarchyData, scopedManager.position_id)
        : hierarchyData
      applySearch(value, base)
    }, 200)
  }, [hierarchyData, scopedManager, applySearch, scopeToManager])

  // ── S971: scope-to-manager handler ───────────────────────────────────────
  const handleScopeManager = useCallback((mgr) => {
    setScopedManager(mgr)
    setSearch('')
    preSearchExpandRef.current = null
    if (!mgr) {
      setFilteredTree(hierarchyData)
      setExpandedNodes(buildDefaultExpanded(hierarchyData))
    } else {
      const scoped = scopeToManager(hierarchyData, mgr.position_id)
      setFilteredTree(scoped)
      // Expand the scoped root and its immediate children
      setExpandedNodes(buildDefaultExpanded(scoped))
    }
  }, [hierarchyData, scopeToManager, buildDefaultExpanded])

  // Toggle expand/collapse of a single node
  const toggleNode = useCallback((positionId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev)
      next.has(positionId) ? next.delete(positionId) : next.add(positionId)
      return next
    })
  }, [])

  // Retained for legacy compatibility (sort/page no longer used in hierarchy view)
  const handleSort = useCallback(() => {}, [])
  const handlePageChange = useCallback(() => {}, [])
  const handlePageSizeChange = useCallback(() => {}, [])

  const goNext = () => {
    window.dispatchEvent(new CustomEvent('decisionflow:navigate', {
      detail: { screen: SCREEN_ORDER[4] }
    }))
  }

  // ── Derived display values ────────────────────────────────────────────────
  const displayTotals = aggregateTotals ?? { total: 0, quota: 0, credit: 0, attainment_avg: null, avg_payout_pct_ote: null }
  const displayCount  = aggregateTotals?.count ?? null
  const hasTree       = filteredTree.length > 0

  // ── Filter helpers ────────────────────────────────────────────────────────
  const removeFilter = (key, value) => {
    applyFilters({ ...filters, [key]: filters[key].filter(v => v !== value) })
  }
  const clearAllFilters = () => applyFilters({ bu: [], segment: [], role: [], motion: [], manager_position_id: null })

  // Search result count — number of _matched nodes in filteredTree
  const matchCount = useMemo(() => {
    if (!search.trim()) return null
    let count = 0
    function countMatches(nodes) {
      for (const n of nodes) {
        if (n._matched) count++
        countMatches(n.children ?? [])
      }
    }
    countMatches(filteredTree)
    return count
  }, [search, filteredTree])

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="space-y-5">

      {/* Header */}
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">EXEC-04</p>
        <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Payout Results</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">
          Authoritative payout-of-record using is_latest_final=true.
          All figures shown for <span className="font-medium text-[rgb(var(--color-text))]">{periodCode}</span>.
          Click a row for full detail.
        </p>
      </header>

      {/* Aggregate tiles */}
      <AggregateTiles
        totals={displayTotals}
        loading={aggregateLoading}
        periodCode={periodCode}
        positionCount={displayCount}
      />

      {/* ── S971 Toolbar — unified org tree controls ────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">

        {/* Refresh */}
        <button
          type="button"
          onClick={() => {
            setScopedManager(null)
            setSearch('')
            preSearchExpandRef.current = null
            loadHierarchy(filters)
            fetchAggregateTotals(filters)
          }}
          disabled={hierarchyLoading}
          className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          {hierarchyLoading ? 'Loading…' : 'Refresh'}
        </button>

        {/* Name search — client-side contextual tree filter */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className="pl-8 pr-8 py-2 rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] w-56"
          />
          {search && (
            <button
              type="button"
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Search result count badge */}
        {search.trim() && (
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {matchCount === 0
              ? 'No matches'
              : `${matchCount} match${matchCount === 1 ? '' : 'es'} — shown in context`}
          </span>
        )}

        {/* Scope-to-manager — S971: scopes entire tree to one manager's branch */}
        <div className="ml-auto">
          <ManagerTypeahead
            fiscalPeriodId={periodId}
            value={scopedManager}
            onChange={handleScopeManager}
            onClear={() => handleScopeManager(null)}
            placeholder="Scope to manager…"
          />
        </div>

        {/* Filter toggle */}
        <button
          type="button"
          onClick={() => setFiltersOpen(o => !o)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded border text-tok-sm transition
            ${filtersOpen || activeFilterCount > 0
              ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/8'
              : 'border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
        >
          <Filter className="h-3.5 w-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 rounded-full bg-[rgb(var(--color-primary))] text-white text-tok-xs px-1.5 py-0.5 leading-none">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Scoped manager indicator */}
      {scopedManager && (
        <div className="flex items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          <Users className="h-3.5 w-3.5 text-[rgb(var(--color-primary))]" />
          <span>
            Showing <span className="font-medium text-[rgb(var(--color-text))]">{scopedManager.position_name}</span>'s org.
          </span>
          <button
            type="button"
            onClick={() => handleScopeManager(null)}
            className="text-[rgb(var(--color-primary))] underline hover:opacity-70"
          >
            Show full org
          </button>
        </div>
      )}

      {/* Filter bar */}
      {filtersOpen && (
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
          <MultiSelectDropdown
            label="Business Unit"
            options={filterOptions.bus}
            selected={filters.bu}
            onChange={v => applyFilters({ ...filters, bu: v })}
          />
          <MultiSelectDropdown
            label="Segment"
            options={filterOptions.segments}
            selected={filters.segment}
            onChange={v => applyFilters({ ...filters, segment: v })}
          />
          <MultiSelectDropdown
            label="Role"
            options={filterOptions.roles}
            selected={filters.role}
            onChange={v => applyFilters({ ...filters, role: v })}
          />
          <MultiSelectDropdown
            label="Selling Motion"
            options={filterOptions.motions}
            selected={filters.motion}
            onChange={v => applyFilters({ ...filters, motion: v })}
          />
        </div>
      )}

      {/* Active filter chips — BU/Segment/Role/Motion only; scope-to-manager shown above */}
      <ActiveFilterChips
        filters={{ ...filters, manager_position_id: null }}
        filterOptions={filterOptions}
        onRemoveBu={v       => removeFilter('bu', v)}
        onRemoveSegment={v  => removeFilter('segment', v)}
        onRemoveRole={v     => removeFilter('role', v)}
        onRemoveMotion={v   => removeFilter('motion', v)}
        onClearManager={null}
        onClearAll={clearAllFilters}
      />

      {/* Error */}
      {hierarchyError && (
        <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">
          {hierarchyError}
        </div>
      )}

      {/* Loading skeleton */}
      {hierarchyLoading && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-9 rounded bg-[rgb(var(--color-surface-muted))] animate-pulse"
              style={{ opacity: 1 - i * 0.12 }}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!hierarchyLoading && !hierarchyError && filteredTree.length === 0 && hierarchyData.length > 0 && search.trim() && (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-5 py-8 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No positions match <span className="font-medium text-[rgb(var(--color-text))]">"{search}"</span>.
          </p>
          <button
            type="button"
            onClick={() => handleSearchChange('')}
            className="mt-2 text-tok-sm text-[rgb(var(--color-primary))] underline"
          >
            Clear search
          </button>
        </div>
      )}

      {/* ── S971: Unified org tree table ────────────────────────────────────── */}
      {!hierarchyLoading && !hierarchyError && hasTree && (
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-24rem)] bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg">
          <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
            <thead className="bg-[rgb(var(--color-surface-muted))] sticky top-0 z-10">
              <tr>
                {/* Name — widest column, absorbs indentation */}
                <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider whitespace-nowrap">
                  Own Payout
                </th>
                <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider whitespace-nowrap">
                  Team Payout
                </th>
                <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider whitespace-nowrap">
                  Total
                </th>
                <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider whitespace-nowrap">
                  Payout % OTE
                  <span className="ml-1 normal-case font-normal opacity-60">({periodCode})</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))]">
              {filteredTree.map(node => (
                <HierarchyRow
                  key={node.position_id ?? node.payout_summary_id}
                  node={node}
                  depth={0}
                  expanded={expandedNodes}
                  onToggle={toggleNode}
                  onSelect={setSelectedRow}
                  selectedId={selectedRow?.payout_summary_id}
                  ackMap={ackMap}
                  periodCode={periodCode}
                  searchQuery={search}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Acknowledgment legend */}
      {Object.values(ackMap).some(v => v === false) && (
        <p className="flex items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[rgb(var(--color-warning))]" />
          Reps with this indicator have not yet acknowledged their plan — payouts are hidden from them until they do.
        </p>
      )}

      {/* Footer */}
      <footer className="pt-4 border-t border-[rgb(var(--color-border))]">
        <button
          type="button"
          onClick={goNext}
          disabled={!hasTree}
          className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          Continue to statement batches
        </button>
      </footer>

      {/* Payout explanation panel — full layered detail */}
      {/* C8 S955: local_currency_code + total_payout_local passed through */}
      <PayoutExplanationPanel
        payoutSummaryId={selectedRow?.payout_summary_id}
        positionName={selectedRow?.position_name}
        roleCode={selectedRow?.role_code}
        periodLabel={selectedRow?.fiscal_period_id?.toString()}
        localCurrencyCode={selectedRow?.local_currency_code ?? null}
        totalPayoutLocal={selectedRow?.total_payout_local ?? null}
        onClose={() => setSelectedRow(null)}
      />
    </section>
  )
}
